import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useState } from 'react';
// Components
import { Add, Close, Search } from '@mui/icons-material';
import { Button, ButtonGroup, Divider, Fade, Grid, TextField, Typography } from '@mui/material';
import { Button as SMButton, Loader, Modal, Table, Toast } from '../../../components';
import { deleteAdminAttorneyOfficeUser, updateAdminAttorneyOfficeUser } from '../../../fetch';
import { IAttorneyOffice, IAttorneyOfficeUser, IPeople } from '../../../models';
import { MobileUser } from './MobileUser';
import { getCommonPeople } from '../../../fetch/lookups';

interface IAttorneyOfficeUserModal {
  open: boolean;
  onClose: () => void;
  currentAttorneyOffice?: IAttorneyOffice;
  users: IAttorneyOfficeUser[];
  isLoadingUsers: boolean;
  fetchAttorneyOfficeUsers: (id: number) => void;
}

export const AttorneyOfficeUserModal: FC<IAttorneyOfficeUserModal> = ({
  open,
  onClose,
  currentAttorneyOffice,
  users,
  isLoadingUsers,
  fetchAttorneyOfficeUsers
}) => {
  const classes = useStyles();
  const [isError, showError] = useState<boolean>(false);
  const [isSuccess, showSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isAdding, setAdding] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [isSearching, setSearching] = useState<boolean>(false);
  const [hasSearched, setSearched] = useState<boolean>(false);
  const [nameValue, setNameValue] = useState<string>('');
  const [workplaceValue, setWorkplaceValue] = useState<string>('');
  const [emailValue, setEmailValue] = useState<string>('');
  const [searchedUsers, setSearchedUsers] = useState<IPeople[] | null>(null);
  const addedUserIds = users.map(u => u.personId);
  const findUsers = async () => {
    try {
      setSearching(true);
      const res = await getCommonPeople({
        name: nameValue,
        workplace: workplaceValue,
        email: emailValue,
        personType: 'Attorney'
      });
      setSearched(true);

      setSearchedUsers(res.records.filter(r => !addedUserIds.includes(r.personId)));
    } catch (error) {
      console.log(error);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setNameValue('');
    setEmailValue('');
    setWorkplaceValue('');
    setSearchedUsers(null);
    setSearched(false);
  };

  const handleUserAdd = async (user: IPeople) => {
    try {
      setAdding(true);
      await updateAdminAttorneyOfficeUser(currentAttorneyOffice.attorneyOfficeId, user.personId);
      showSuccess(true);
      setSuccessMessage('User Added!');
      fetchAttorneyOfficeUsers(currentAttorneyOffice.attorneyOfficeId);
      setSearchedUsers(searchedUsers.filter(r => r.personId !== user.personId));
    } catch (error) {
      console.log(error);
      showError(true);
      setErrorMessage('Error adding user to attorney office, please try again.');
    } finally {
      setAdding(false);
    }
  };

  const handleUserRemove = async (user: IPeople) => {
    const result = window.confirm('Are you sure you want to delete this user?');
    if (result) {
      setDeleting(true);
      try {
        await deleteAdminAttorneyOfficeUser(currentAttorneyOffice.attorneyOfficeId, user.personId);
        showSuccess(true);
        setSuccessMessage('User Removed!');
        fetchAttorneyOfficeUsers(currentAttorneyOffice.attorneyOfficeId);
      } catch (error) {
        setErrorMessage(error);
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = (isSearch?: boolean) => [
    {
      Header: 'First Name',
      accessor: 'firstName',
      overrideWidth: 150,
      sort: false
    },
    {
      Header: 'Last Name',
      accessor: 'lastName',
      overrideWidth: 150,
      sort: false
    },
    {
      Header: 'Status',
      accessor: 'activeStatus',
      overrideWidth: 300,
      sort: false
    },
    {
      Header: 'Email',
      accessor: 'email',
      sort: false
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      overrideWidth: 200,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: any } };
      }) => {
        return isSearch ? (
          <SMButton
            id="find-users"
            type="button"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleUserAdd(original)}
          >
            Add Attorney User
          </SMButton>
        ) : (
          <>
            <Button
              id="remove"
              color="primary"
              startIcon={<Close />}
              className={classes.deleteButton}
              onClick={() => handleUserRemove(original)}
            >
              Remove
            </Button>
          </>
        );
      }
    }
  ];
  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          onClose();
          clearSearch();
        }}
        maxWidth="md"
      >
        {(isLoadingUsers || isAdding || isDeleting) && (
          <Loader position="centered" type="overlay">
            Loading...
          </Loader>
        )}
        <Fade in={open}>
          <div>
            <Typography variant="h5">
              Add & Remove Attorney User To{' '}
              {currentAttorneyOffice && currentAttorneyOffice.workplace}
            </Typography>
            <Divider />
            <div className={classes.content}>
              <div className={classes.searchWrapper}>
                <Typography gutterBottom>Search for attorney users</Typography>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      autoComplete="nope"
                      label="Name"
                      name="name"
                      value={nameValue}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          findUsers();
                        }
                      }}
                      onChange={e => {
                        setNameValue(e.target.value);
                      }}
                    />
                  </Grid>                  
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      autoComplete="nope"
                      label="Email"
                      name="email"
                      value={emailValue}
                      onChange={e => setEmailValue(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <ButtonGroup>
                      <SMButton
                        id="find-users"
                        type="button"
                        variant="contained"
                        color="primary"
                        startIcon={<Search />}
                        onClick={findUsers}
                      >
                        Search
                      </SMButton>
                      {hasSearched && (
                        <SMButton
                          id="clear-search"
                          type="button"
                          variant="contained"
                          color="inherit"
                          startIcon={<Close />}
                          onClick={async () => {
                            clearSearch();
                          }}
                        >
                          Clear Search
                        </SMButton>
                      )}
                    </ButtonGroup>
                  </Grid>
                </Grid>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  className={classes.searchedUsersWrapper}
                >
                  <div className={classes.loaderWrapper}>
                    {isSearching && <Loader position="centered" />}
                    {!isSearching && searchedUsers && searchedUsers.length > 0 && (
                      <Typography variant="caption">
                        Search results, scroll to find the user you want to add to the attorney
                        office.
                      </Typography>
                    )}
                  </div>
                  {searchedUsers && !isSearching && (
                    <Table
                      columns={columns(true)}
                      data={searchedUsers}
                      hidePagination
                      mobileProps={{
                        handleAdd: (user: IPeople) => handleUserAdd(user)
                      }}
                      ResponsiveComponent={MobileUser}
                    />
                  )}
                </Grid>
              </div>
              {searchedUsers?.length === 0 && <Divider />}
              <div className={classes.addedUsersWrapper}>
                <Typography variant="h6">
                  Users Added in {currentAttorneyOffice && currentAttorneyOffice.workplace}
                </Typography>
                <Table
                  columns={columns(false)}
                  data={users}
                  hidePagination
                  mobileProps={{
                    handleDelete: (user: IPeople) => handleUserRemove(user)
                  }}
                  ResponsiveComponent={MobileUser}
                />
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
      <Toast
        id="user-success"
        message={successMessage || `Attorney Users Updated!`}
        open={isSuccess}
        onClose={() => showSuccess(false)}
        variant="success"
      />
      <Toast
        id="user-error"
        autoHideDuration={5000}
        message={
          errorMessage ||
          'We were unable to update the attorney users at this time. Please try again later.'
        }
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  content: {
    marginTop: theme.spacing(1)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  saveWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  addButton: {
    flex: 1
  },
  validationMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  searchWrapper: {
    marginBottom: theme.spacing(1)
  },
  addedUsersWrapper: {
    marginTop: theme.spacing(1)
  },
  searchedUsersWrapper: {
    maxHeight: `20rem`,
    overflow: 'auto'
  },
  loaderWrapper: {
    marginTop: theme.spacing(0.5)
  }
}));
